"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const common_utils_1 = require("@zapier/common-utils");
const core_1 = require("@emotion/core");
const design_system_1 = require("@zapier/design-system");
const AppListItem_1 = __importDefault(require("../AppListItem"));
const EmptySearchResults_1 = __importDefault(require("../EmptySearchResults"));
const AppListLoader_1 = __importDefault(require("./AppListLoader"));
const useFreezeHeight_1 = __importDefault(require("../../../hooks/useFreezeHeight"));
const containerCss = core_1.css `
  background-color: ${design_system_1.Colors.grey000};
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  padding: 20px;
  justify-content: flex-start;

  > * + * {
    margin-top: 20px;
  }
`;
const headerContainer = core_1.css `
  display: flex;
  align-items: center;
  padding: 10px 10px 0px 10px;
`;
const searchContainer = core_1.css `
  padding: 0px 10px;
`;
const headerLogo = core_1.css `
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  border: 1px solid ${design_system_1.Colors.brand};
  border-radius: 4px;
`;
const appListItemsWrapperCss = core_1.css `
  overflow-y: auto;
  padding: 0px 10px;
`;
const appListItemsCss = ({ hasApps, isLoading, isSearching, }) => {
    if (isLoading) {
        return core_1.css `
      display: grid;
      gap: 20px;
      grid-template-columns: 1fr;

      @media screen and (min-width: 600px) {
        grid-template-columns: repeat(2, 1fr);
      }
    `;
    }
    return core_1.css `
    display: grid;
    grid-template-columns: 1fr;
    height: ${hasApps ? 'auto' : '100%'};

    @media screen and (min-width: 600px) {
      grid-template-columns: ${hasApps && !isSearching
        ? 'repeat(2, 1fr)'
        : '1fr'};
    }
  `;
};
const bumperCss = core_1.css `
  text-align: center;
  font-size: 0.875em;
  margin-top: 20px;
  opacity: 0.6;
`;
function AppList({ apps, isTrigger = true, onSelect, searchQuery, onSearchQueryChange, onNoAppFound, }) {
    const hasApps = apps.length > 0;
    const isLoading = !hasApps && !searchQuery;
    const isSearching = Boolean(searchQuery);
    const { elementRef: appListRef, style: appListStyle } = useFreezeHeight_1.default({ isLoading });
    const searchInputRef = react_1.useRef(null);
    const focusInput = () => {
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };
    react_1.useEffect(() => {
        focusInput();
    }, []);
    const onSearchInputChange = react_1.useCallback(e => onSearchQueryChange(e.target.value), [onSearchQueryChange]);
    const onSearchClear = react_1.useCallback(() => onSearchQueryChange(''), [
        onSearchQueryChange,
    ]);
    function handleNoAppFound() {
        onSearchClear();
        focusInput();
        onNoAppFound();
    }
    return (common_utils_1.isolateComponent("div", { css: containerCss },
        common_utils_1.isolateComponent("div", { css: headerContainer },
            common_utils_1.isolateComponent("div", { css: headerLogo },
                common_utils_1.isolateComponent(design_system_1.Icon, { color: "brand", name: "navApps", size: 28 })),
            common_utils_1.isolateComponent("div", null,
                common_utils_1.isolateComponent("div", null,
                    common_utils_1.isolateComponent(design_system_1.Text, { color: "black", fontWeight: 600, type: "bodyText800" }, "App Event")),
                common_utils_1.isolateComponent("div", null,
                    common_utils_1.isolateComponent(design_system_1.Text, { color: "grey700", type: "altText900" }, isTrigger
                        ? 'Start the Zap when something happens in an app'
                        : 'Do something in an app')))),
        common_utils_1.isolateComponent("div", { css: searchContainer },
            common_utils_1.isolateComponent(design_system_1.TextInput, { ariaLabel: "Search apps\u2026", autoComplete: "off", name: "appSearch", onChange: onSearchInputChange, placeholder: "Search apps\u2026", ref: searchInputRef, renderIconAfter: ({ iconSize }) => {
                    return (common_utils_1.isolateComponent("button", { "aria-label": "Clear search", onClick: onSearchClear },
                        common_utils_1.isolateComponent(design_system_1.Icon, { isBlock: true, name: "x", size: iconSize })));
                }, renderIconBefore: ({ iconSize }) => {
                    return common_utils_1.isolateComponent(design_system_1.Icon, { name: "magnifyingGlass", size: iconSize });
                }, value: searchQuery })),
        common_utils_1.isolateComponent("div", { css: appListItemsWrapperCss, ref: appListRef, style: appListStyle },
            common_utils_1.isolateComponent("div", { css: appListItemsCss({
                    hasApps,
                    isLoading,
                    isSearching,
                }) }, hasApps ? (apps.map(app => (common_utils_1.isolateComponent(AppListItem_1.default, { app: app, isSearching: isSearching, key: app.key, onSelect: onSelect })))) : isLoading ? (common_utils_1.isolateComponent(AppListLoader_1.default, null)) : (common_utils_1.isolateComponent(EmptySearchResults_1.default, { onNoAppFound: handleNoAppFound, query: searchQuery, type: isTrigger ? 'trigger' : 'action' }))),
            hasApps && !searchQuery && (common_utils_1.isolateComponent("p", { css: bumperCss }, "\u2026and over 2,000+ more")))));
}
exports.default = AppList;
